///
/// EarthBrowser
///
/// Created by Matt Giger on 11/3/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Local mirror of the `cloud_forecast` dataset: one row per (product,
/// valid time) holding the newest run's frame. Products are the RRFS
/// simulated band-13 frame over CONUS ("rrfs-ir13", hourly) and the
/// global ECMWF model-cloud frame ("ecmwf-cloud", 3-hourly).
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

export const cCloudForecastProductIr13 = "rrfs-ir13"
export const cCloudForecastProductEcmwf = "ecmwf-cloud"

export interface CloudForecastFrameRecord extends DatasetFeature {
	id: string // product:valid_time
	product: string
	run_time: string
	valid_time: string
	forecast_minutes: number
	image_url: string
	source_path: string
	size_bytes: number
	width: number
	height: number
	vmin: number
	vmax: number
	bounds_wgs84: string
	created_at: string
}

const cBatchSize = 500

export class CloudForecastTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists cloud_forecast_frames (
					id text primary key,
					product text not null,
					run_time text not null,
					valid_time text not null,
					forecast_minutes integer not null,
					image_url text not null,
					source_path text not null,
					size_bytes integer not null,
					width integer not null,
					height integer not null,
					vmin real not null,
					vmax real not null,
					bounds_wgs84 text not null,
					created_at text not null
				)`
			},
			{ sql: "create index if not exists idx_cloud_forecast_frames_product_valid on cloud_forecast_frames(product, valid_time)" },
		])
	}

	public static async replaceAll(rows: CloudForecastFrameRecord[]): Promise<void> {
		await Database.db.exec("delete from cloud_forecast_frames", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: CloudForecastFrameRecord[]): Promise<void> {
		const statement = `insert into cloud_forecast_frames (
				id, product, run_time, valid_time, forecast_minutes,
				image_url, source_path, size_bytes,
				width, height, vmin, vmax,
				bounds_wgs84, created_at
			) values (
				?, ?, ?, ?, ?,
				?, ?, ?,
				?, ?, ?, ?,
				?, ?
			)
			on conflict(id) do update set
				product = excluded.product,
				run_time = excluded.run_time,
				valid_time = excluded.valid_time,
				forecast_minutes = excluded.forecast_minutes,
				image_url = excluded.image_url,
				source_path = excluded.source_path,
				size_bytes = excluded.size_bytes,
				width = excluded.width,
				height = excluded.height,
				vmin = excluded.vmin,
				vmax = excluded.vmax,
				bounds_wgs84 = excluded.bounds_wgs84,
				created_at = excluded.created_at`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(r => [
				r.id, r.product, r.run_time, r.valid_time, r.forecast_minutes,
				r.image_url, r.source_path, r.size_bytes,
				r.width, r.height, r.vmin, r.vmax,
				r.bounds_wgs84, r.created_at,
			]))
		}
	}

	/** Frames valid at or after `sinceIso`, oldest first */
	public static async getValidSince(sinceIso: string, limit = 500): Promise<CloudForecastFrameRecord[]> {
		const rows = await Database.db.exec(
			`select
				id, product, run_time, valid_time, forecast_minutes,
				image_url, source_path, size_bytes,
				width, height, vmin, vmax,
				bounds_wgs84, created_at
			from cloud_forecast_frames
			where valid_time >= ?
			order by product, valid_time asc
			limit ${Math.max(1, Math.min(limit, 5000))}`,
			[sinceIso],
			"high"
		) as CloudForecastFrameRecord[]
		return rows
	}
}
